// Assignment question sets, authored in-repo (file-based authoring per the
// build plan). Keyed by the lesson `body_ref` so a workbook/assignment lesson
// can carry both prose (its Markdown) and a structured question list.
//
// UI question types (per Day 4 scope):
//   - multiple_choice  → renders radio choices
//   - true_false       → renders True/False
//   - numeric          → numeric input with a tolerance band (W&B, density-alt,
//                        fuel planning, power/leaning calcs)
//   - free_response    → textarea (scenario / short answer)
//
// ─────────────────────────────────────────────────────────────────────────────
// SECURITY (Day 7 GATE 1): NO ANSWER KEYS LIVE HERE.
//
// This file ships in the browser bundle, so it carries ONLY what the UI needs to
// RENDER a question — prompt, type, choices, unit, tolerance, points. The correct
// answers were moved OUT to a SERVER-ONLY module (functions/_lib/answer-keys.ts)
// that is never imported by any src/** client code. Authoritative grading happens
// in the Worker functions/api/grade.ts, which loads those keys, scores with the
// shared pure core, and persists with the Supabase service-role key. A student can
// therefore no longer read the answer key from the JS bundle, nor forge a passing
// score (the score column is written by the server, not the student's session).
//
// IMPORTANT: do NOT add an `answer` field back to this type or these objects — it
// would re-leak the key into the client bundle. Keys belong in answer-keys.ts.
// ─────────────────────────────────────────────────────────────────────────────
//
// NOTE on schema mapping: 0001 `questions.type` uses ('mc'|'tf'|'numeric'|'short')
// and `questions.quiz_id` is NOT NULL, so assignment questions cannot live in the
// `questions` table without a quiz. For Day 4 we author assignment questions here
// (files) and persist answers to `submissions` keyed by (enrollment_id, lesson_id,
// content question id) with question_id = NULL. Quiz-backed auto-grading is Day 5.

export type AssignmentQuestionType =
  | 'multiple_choice'
  | 'true_false'
  | 'numeric'
  | 'free_response';

export type AssignmentQuestion = {
  /** Stable id within the assignment; used as the submission key AND the key
   *  that the server-side answer-key module is keyed on (bodyRef + qid). */
  id: string;
  type: AssignmentQuestionType;
  prompt: string;
  /** Optional helper text shown under the prompt. */
  help?: string;
  /** multiple_choice only. */
  choices?: string[];
  /** numeric only — unit label shown beside the input (e.g. "gal", "lb"). */
  unit?: string;
  /**
   * numeric only — accepted +/- tolerance for auto-grading. This is NOT secret
   * (it does not reveal the answer), so it stays client-side; the server reads
   * the SAME tolerance from its key module to stay authoritative.
   */
  tolerance?: number;
  /**
   * Points this question contributes to the auto-gradable total. Defaults to 1.
   * free_response questions never contribute to the auto-gradable total
   * regardless of this value. Not secret — safe to ship.
   */
  points?: number;
  // NOTE: there is intentionally NO `answer` field. Answer keys are server-only
  // (functions/_lib/answer-keys.ts). See the SECURITY block above.
};

export type AssignmentDef = {
  /** Matches the lesson `body_ref`. */
  bodyRef: string;
  title: string;
  questions: AssignmentQuestion[];
};

export const assignments: AssignmentDef[] = [
  {
    bodyRef: 'content/block-1.2.md',
    title: 'Fuel System Workbook',
    questions: [
      {
        id: 'kc1',
        type: 'multiple_choice',
        prompt:
          'On a 210 with a LEFT / RIGHT / BOTH fuel selector, which position is normally used for takeoff and landing?',
        choices: ['LEFT', 'RIGHT', 'BOTH', 'OFF'],
      },
      {
        id: 'kc2',
        type: 'true_false',
        prompt:
          'The auxiliary (electric boost) pump can be used for priming and as a backup to the engine-driven pump.',
      },
      {
        id: 'sc1',
        type: 'free_response',
        prompt:
          'Two hours into a cross-country the fuel-flow gauge on a hot day reads erratically and the engine surges briefly when you switch from BOTH to LEFT. Describe the actions you would take, in order, and your reasoning.',
        help: 'Think tank selection, boost pump, mixture, and where you would land.',
      },
      {
        id: 'calc1',
        type: 'numeric',
        prompt:
          'Usable fuel is 87 gal. At a planned cruise burn of 16.5 gph, how many hours of endurance do you have before reserves? Round to one decimal.',
        unit: 'hr',
        tolerance: 0.2,
      },
      {
        id: 'calc2',
        type: 'numeric',
        prompt:
          'For a 3.5 hr flight at 16.5 gph plus a 45-minute VFR reserve at the same burn, how many gallons must be on board at takeoff?',
        unit: 'gal',
        tolerance: 1.0,
      },
    ],
  },
  {
    bodyRef: 'content/block-1.2-workbook.md',
    title: 'Fuel System Workbook',
    questions: [
      {
        id: 'kc1',
        type: 'multiple_choice',
        prompt: 'Which engine is fitted to the fuel-injected 210?',
        choices: [
          'Lycoming O-360',
          'Continental IO-520 / IO-550',
          'Lycoming IO-720',
          'Continental O-200',
        ],
      },
      {
        id: 'kc2',
        type: 'true_false',
        prompt: 'Running a fuel tank dry is a common cause of fuel-related incidents in high-performance singles.',
      },
      {
        id: 'sc1',
        type: 'free_response',
        prompt:
          'You are planning a 4.5 hr leg with both tanks topped off (usable 87 gal total). Describe your tank-switching plan and how you will keep the airplane in balance and avoid running a tank dry.',
      },
      {
        id: 'calc1',
        type: 'numeric',
        prompt: 'At 17 gph, how many gallons does a 4.5 hr leg consume? Round to the nearest gallon.',
        unit: 'gal',
        tolerance: 1.0,
      },
    ],
  },
];

const byRef = new Map(assignments.map((a) => [a.bodyRef, a]));

/** Resolve an assignment question set by lesson `body_ref`. */
export function getAssignment(bodyRef: string | null | undefined): AssignmentDef | null {
  if (!bodyRef) return null;
  return byRef.get(bodyRef) ?? byRef.get(bodyRef.replace(/^\//, '')) ?? null;
}
